import { AxiosRequestConfig } from 'axios';
import { HttpResponse, ajax } from '../../../../common/ajax';
import { config } from '../../config';
import {
  Budget,
  ICustomerInfoContext,
  IPlatformInformation,
} from '../../contexts/common';
import {
  AccountInfoResponse,
  AddressOption,
  ChangePasswordModel,
  ChangePasswordResponse,
  CustomerBalance,
  CustomerInvoicingAddressCommand,
  UpdateAccountInfoData,
  UpdateDeliveryAddressData,
} from '../../models/Customers';
import { DemoAccountRequestData } from '../../models/DemoAccount';

interface ICustomerService {
  getWhoAmI: (
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<ICustomerInfoContext>>;
  getCustomerBalance: (
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<CustomerBalance>>;
  getCustomerBalanceSummary: (
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<Budget>>;
  getCustomerPlatformInformation: (
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<IPlatformInformation>>;
  getAccountInfo: (
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<AccountInfoResponse>>;
  getAddressOptions: (
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<AddressOption[]>>;
  updateAccountInfo: (
    data: UpdateAccountInfoData,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<boolean>>;
  updateDeliveryAddress: (
    data: UpdateDeliveryAddressData,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<boolean>>;
  updateLanguage: (
    languageCode: string,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<boolean>>;
  updateCustomerPassword: (
    data: ChangePasswordModel,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<ChangePasswordResponse>>;
  checkOnboarding: (
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<boolean>>;
  createDemoCustomer: (
    data: DemoAccountRequestData,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<boolean>>;
  updateInvoicingAddress: (
    data: CustomerInvoicingAddressCommand,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<boolean>>;
  validateInvoicingAddress: (
    data: CustomerInvoicingAddressCommand,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<boolean>>;
}

export const customerService: ICustomerService = {
  getWhoAmI(axiosConfig?: AxiosRequestConfig) {
    return ajax.get<ICustomerInfoContext>(
      `${config.benefitApiUrl}/who-am-i`,
      axiosConfig,
    );
  },
  getCustomerBalance(axiosConfig) {
    return ajax.get<CustomerBalance>(
      `${config.benefitApiUrl}/customers/balance`,
      axiosConfig,
    );
  },
  getCustomerBalanceSummary(axiosConfig) {
    return ajax.get<Budget>(
      `${config.benefitApiUrl}/customers/balance-summary`,
      axiosConfig,
    );
  },
  getCustomerPlatformInformation(axiosConfig) {
    return ajax.get<IPlatformInformation>(
      `${config.benefitApiUrl}/customers/monthly-platform-timeline`,
      axiosConfig,
    );
  },
  getAccountInfo(axiosConfig) {
    return ajax.get<AccountInfoResponse>(
      `${config.benefitApiUrl}/customers/account-info`,
      axiosConfig,
    );
  },
  getAddressOptions(axiosConfig) {
    return ajax.get<AddressOption[]>(
      `${config.benefitApiUrl}/customers/address-options`,
      axiosConfig,
    );
  },
  updateAccountInfo(data, axiosConfig) {
    return ajax.put<boolean>(
      `${config.benefitApiUrl}/customers/account-info`,
      data,
      axiosConfig,
    );
  },
  updateDeliveryAddress(data, axiosConfig) {
    return ajax.put<boolean>(
      `${config.benefitApiUrl}/customers/delivery-address`,
      data,
      axiosConfig,
    );
  },
  updateLanguage(languageCode, axiosConfig) {
    return ajax.put<boolean>(
      `${config.benefitApiUrl}/customers/change-language`,
      { languageCode },
      axiosConfig,
    );
  },
  updateCustomerPassword(data, axiosConfig) {
    return ajax.post<ChangePasswordResponse>(
      `${config.benefitApiUrl}/customers/change-password`,
      data,
      axiosConfig,
    );
  },
  checkOnboarding(axiosConfig) {
    return ajax.put<boolean>(
      `${config.benefitApiUrl}/customers/onboarding`,
      axiosConfig,
    );
  },
  createDemoCustomer(data, axiosConfig) {
    return ajax.post<boolean>(
      `${config.benefitApiUrl}/customers/create-demo`,
      data,
      axiosConfig,
    );
  },
  updateInvoicingAddress(data, axiosConfig) {
    return ajax.put<boolean>(
      `${config.benefitApiUrl}/customers/invoicing-address`,
      data,
      axiosConfig,
    );
  },
  validateInvoicingAddress(data, axiosConfig) {
    return ajax.put<boolean>(
      `${config.benefitApiUrl}/customers/invoicing-address/validate`,
      data,
      axiosConfig,
    );
  },
};
